import { useQuery } from "@tanstack/react-query";

import { canonicalShopDomain } from "../api/client";
import { getMe } from "../api/me";
import { isStockyPublicBigIntId } from "../api/stocky_public_ids";
import { type Feature, planAllows } from "../lib/entitlements";

export interface UsePlanResult {
  shopId: string | null;
  shopDomain: string | null;
  shopGeneration: string | null;
  planCode: string | null;
  isActive: boolean;
  /** 商品数超過も backend の feature router と同じく利用不可として扱う。 */
  isProductLimitExceeded: boolean;
  /** 商品数超過の解消に必要な plan。通常の feature minimum より優先して表示する。 */
  productLimitRequiredPlanCode: string | null;
  isLoading: boolean;
  /** /api/me の取得失敗。true のときの planCode/allows は「不明」であって「free」ではない。 */
  isError: boolean;
  /** 取得失敗からの再試行（QueryErrorBanner の onRetry に渡す）。 */
  retry: () => void;
  allows: (feature: Feature) => boolean;
}

/**
 * Read the authoritative shop's plan from `/api/me` and expose a UX-only
 * entitlement check. The underlying query is partitioned by the canonical
 * embedded-shop generation, so another shop's cached plan is never reused.
 *
 * `allows` mirrors backend `get_active_plan_code`: a plan entitles a feature
 * ONLY while the subscription is ACTIVE (trial counts). FROZEN/cancelled →
 * treated as free (null), matching the 403 the backend would return.
 */
export function usePlan(): UsePlanResult {
  // Production mounts this hook only inside ShopGenerationBoundary, whose
  // tenant-owned QueryClient is freshly seeded with the verified /api/me row.
  // Re-running the bootstrap authority query here would create a second cache
  // and can leave every plan-gated screen waiting after a shop transition.
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
  });
  const responseShopDomain = canonicalShopDomain(data?.shopify_domain);
  const shopId =
    data !== undefined &&
    typeof data.shop_id === "string" &&
    isStockyPublicBigIntId(data.shop_id)
      ? data.shop_id
      : null;
  const shopDomain = shopId !== null ? responseShopDomain : null;
  const shopGeneration =
    shopId !== null && shopDomain !== null
      ? `${shopId}:${shopDomain}`
      : null;

  const isActive = data?.subscription.active ?? false;
  const planCode = data?.subscription.plan_code ?? null;

  const allows = (feature: Feature): boolean =>
    planAllows(isActive ? planCode : null, feature);

  return {
    shopId,
    shopDomain,
    shopGeneration,
    planCode,
    isActive,
    isProductLimitExceeded: data?.product_limit?.over_limit === true,
    productLimitRequiredPlanCode:
      data?.product_limit?.over_limit === true
        ? data.product_limit.required_plan_code
        : null,
    isLoading,
    isError,
    retry: () => void refetch(),
    allows,
  };
}
